import {SmallLoader} from '@kit';

import React from 'react';

import {useFeatQuery, ImageTarget} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {getApolloError, humanEnumeration, getSourceText} from '../../../components/utils';
import {EditHoc} from '../../../components/edit-hoc';
import {FeatureView} from '../../../components/feature-view';
import {Markdown, MarkdownInline} from '../../../components/utils/md';
import {Gallery} from '../../../components/gallery';
import type {Feat} from '../../../generated/types';
import {OtherTitles} from '../../../components/other-titles';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {FeatSeo} from '../seo';
import {repeatableText} from './repeatable-text';
import {NotFound} from '../../../components/not-found';
import {CommentTargetType, Comments} from '../../../components/comments';

import {PageTitle} from '../../../components/page-title';
import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';

type Props = {
	featId: string;
	title: string;
};

export const FeatView: React.FC<Props> = ({featId, title}) => {
	const {data, loading, error} = useFeatQuery({variables: {id: featId}});
	const feat = data?.feat;
	const notFound = !loading && !feat && isNotFoundError(error);
	useNotFound(notFound);

	return (
		<article className={dashboardCss.readingWrapper}>
			{feat && <FeatSeo feat={feat as Feat} />}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<PageTitle
				subTitle={
					feat && (
						<em>
							<MarkdownInline text={feat.prerequisite || ''} />
						</em>
					)
				}
			>
				{title}
			</PageTitle>
			<div className={dashboardCss.page}>
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{feat && (
					<div>
						<EditHoc
							load={() => import('../edit-buttons').then((m) => m.EditFeat)}
							editProps={{id: feat.id}}
						>
							{feat.features?.length ? (
								<>
									{feat.description && <Markdown text={feat.description ?? ''} />}
									{[...feat.features]
										.sort((a, b) => (a.order || 0) - (b.order || 0))
										.map((feature) => (
											<FeatureView
												key={feature.id}
												feature={feature as any}
												parentObject={{type: 'feat', parentId: feat.id}}
											/>
										))}
									{feat.repeatable && <Markdown text={repeatableText(feat)} />}
								</>
							) : (
								<Markdown text={feat.description ?? ''} />
							)}
							<div className={markdownCss.margin}>
								<div className={markdownCss.indent}>
									<OtherTitles
										originalTitle={feat.originalTitle}
										alternativeTitles={feat.alternativeTitles}
									/>
									<p>
										<b>Источник: </b>
										{getSourceText(feat.source as any)}.
									</p>
								</div>
							</div>
						</EditHoc>
						<Gallery images={feat.images || []} target={{type: ImageTarget.Feat, id: featId}} />
					</div>
				)}
				{feat && <Comments targetType={CommentTargetType.Feat} targetId={featId} />}
			</div>
		</article>
	);
};
